import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

/**
 * Converts a Unix timestamp (seconds or milliseconds) to a formatted date string.
 * @param unixTimestamp - The Unix timestamp (in seconds or milliseconds).
 * @returns Formatted date string in "YYYY-MM-DD HH:mm" format.
 */
export function formatUnixTimestamp(unixTimestamp: number): string {
  // Check if timestamp is in milliseconds and convert to seconds if needed
  const timestampInSeconds =
    unixTimestamp > 1e10 ? Math.floor(unixTimestamp / 1000) : unixTimestamp;

  return dayjs.unix(timestampInSeconds).format('YYYY-MM-DD HH:mm');
}

/**
 * Converts a Unix timestamp (seconds or milliseconds) to a formatted date string.
 * @param unixTimestamp - The Unix timestamp (in seconds or milliseconds).
 * @returns Formatted date string in "YYYY-MM-DD" format.
 */
export function formatUnixTimestampToDate(unixTimestamp: number): string {
  // Check if timestamp is in milliseconds and convert to seconds if needed
  const timestampInSeconds =
    unixTimestamp > 1e10 ? Math.floor(unixTimestamp / 1000) : unixTimestamp;

  return dayjs.unix(timestampInSeconds).format('YYYY-MM-DD');
}

/**
 * Parses a date into YYYY-MM-DD format
 * @param input date in YYYY-MM-DD format or unix timestamp
 * @returns date in YYYY-MM-DD format
 */
export function formatDate(input: string | number): string {
  // Check if input is a valid Unix timestamp (numeric)
  if (
    typeof input === 'number' ||
    (!isNaN(Number(input)) && Number(input) > 0)
  ) {
    return dayjs.unix(Number(input)).format('YYYY-MM-DD');
  }

  // Check if input is already in YYYY-MM-DD format
  if (dayjs(input, 'YYYY-MM-DD', true).isValid()) {
    return input;
  }

  return 'Invalid date';
}

/**
 * Formats a given date based on whether intraday data should be used.
 *
 * @param {string | number | Date} date - The date to be formatted. Can be a string, timestamp, or Date object.
 * @param {boolean} useIntraday - If true, returns a Unix timestamp; otherwise, returns a formatted date string (YYYY-MM-DD).
 * @returns {number | string} - A Unix timestamp if intraday data is used; otherwise, a formatted date string.
 */
export function getFormattedTime(
  date: string | number | Date,
  useIntraday: boolean
): number | string {
  return useIntraday ? dayjs(date).unix() : dayjs(date).format('YYYY-MM-DD');
}

/**
 * Formats a UTC datetime string or Date object to local time,
 * including time component only if it's non-zero in UTC.
 *
 * @param utcDate - The input UTC date as a string or Date
 * @returns Formatted string:
 *          - 'YYYY-MM-DD' if time component in UTC is zero
 *          - 'YYYY-MM-DD HH:mm' if time component in UTC is non-zero
 *
 * @example
 * formatUtcDateWithOptionalTime('2025-08-05T00:00:00Z'); // '2025-08-05'
 * formatUtcDateWithOptionalTime('2025-08-05T14:30:00Z'); // '2025-08-05 16:30'
 */
export function formatUtcDateWithOptionalTime(utcDate: string | Date): string {
  const utc = dayjs.utc(utcDate);
  const local = dayjs(utcDate); // parse normally (local time)

  const hasTimeComponent =
    utc.hour() !== 0 || utc.minute() !== 0 || utc.second() !== 0;

  return hasTimeComponent
    ? local.format('YYYY-MM-DD HH:mm')
    : local.format('YYYY-MM-DD');
}

/**
 * Returns today's date as a string in ISO 8601 date format (YYYY-MM-DD).
 */
export function getTodayAsDateString(): string {
  return dayjs().format('YYYY-MM-DD');
}

/**
 * Formats a date and time as DD/MM/YYYY HH:mm:ss.
 */
export function formatDateTime(date: string | number | Date): string {
  return dayjs(date).format('DD/MM/YYYY HH:mm:ss');
}

/**
 * Formats a date  as DD MMM YYYY.
 */
export function formatDateShort(date: string | number | Date): string {
  return dayjs(date).format('DD MMM YYYY');
}

/**
 * Formats a date as localized MM/DD/YYYY.
 */
export function formatLocalizedDateOnly(date: string | number | Date): string {
  return dayjs(date).format('L');
}

/**
 * Formats the time component of a date as HH:mm:ss.
 */
export function formatTimeOnly(date: string | number | Date): string {
  return dayjs(date).format('HH:mm:ss');
}

/**
 * Normalizes a date received from the API.
 * Some API properties currently use non-nullable DateTime values, meaning missing dates are serialized as DateTime.MinValue (0001-01-01T00:00:00) instead of null.
 *
 * @param date - An ISO date string, null, or undefined.
 * @returns The original date, or null if no meaningful date was provided.
 */
export const normalizeApiDate = (
  date: string | null | undefined
): string | null => {
  if (!date || date.startsWith('0001-01-01T')) {
    return null;
  }

  return date;
};
